import { Fieldset, FocusTrap, InputBase } from "@mantine/core";
import { DatePickerInput, DateValue } from "@mantine/dates";
import { useMemo } from "react";
import { FormProvider, SubmitHandler, useController } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AddressDto } from "#modules/addresses/infra/fetchAddress.api";
import { ClientDto } from "#modules/clients/infra/Client.dto";
import { ContactDto } from "#modules/contacts/infra/fetchContact.api";
import { QuoteRequestDto } from "#modules/quoteRequests/infra/fetchQuoteRequest.api";
import { toUtcMidnight } from "#shared/helpers/dates";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { TextareaController } from "#shared/view/components/TextareaController";
import { Button } from "#shared/view/designSystem/Button";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { TextInputController } from "#shared/view/designSystem/inputs/TextInputController";

export type PdfInputs = {
  client: {
    id: string;
    name: string;
  } | null;
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null;
  billingAddress: {
    id: string;
    street: string;
    zipCode: string;
    city: string;
  } | null;
  expirationDate: Date | null;
  shippingDelay: Date | null;
  comment: string | null;
  clientReference: string | null;
  discountRatio: number | undefined | null;
  salesMarginRatio: number | undefined | null;
};

export const GenerateQuoteForm = ({
  quoteRequest,
  client,
  address,
  contact,
  onSubmit,
  isLoading,
}: {
  quoteRequest: QuoteRequestDto | null | undefined;
  client: ClientDto | null | undefined;
  address: AddressDto | null | undefined;
  contact: ContactDto | null | undefined;
  onSubmit: SubmitHandler<PdfInputs>;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();

  const pdfInputsDefaultValues = useMemo(() => {
    if (quoteRequest) {
      const shippingDelay = quoteRequest.quoteRequestLines[0]?.shippingDelay ?? null;

      return {
        ...quoteRequest,
        client,
        billingAddress: address,
        contact,
        shippingDelay: shippingDelay ? new Date(shippingDelay) : null,
      };
    }

    return undefined;
  }, [quoteRequest, client, address, contact]);

  const formMethods = useForm<PdfInputs>({
    defaultValues: pdfInputsDefaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const { field: clientIdField } = useController({ name: "client.id", control });
  const { field: expirationDateField } = useController({ name: "expirationDate", control });
  const { field: shippingDelayField } = useController({ name: "shippingDelay", control });

  const onHandleDateChange =
    (field: { onChange: (value: Date | null) => void }) => (d: DateValue) => {
      if (!d) {
        return field.onChange(null);
      }
      const utcMidnight = toUtcMidnight(new Date(d));
      field.onChange(utcMidnight);
    };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="xs">
          <InputBase type="hidden" {...clientIdField} />
          <FocusTrap>
            <Flex gap="xs">
              <Fieldset legend={t("quoteRequestLine.generateQuoteModal.legendClient")} flex={1}>
                <Stack gap="xs" flex={1}>
                  <TextInputController
                    name="client.name"
                    control={control}
                    required
                    data-autofocus
                    html5Validation={false}
                    label={t("quoteRequestLine.generateQuoteModal.client.name")}
                    error={errors.client?.name ? t("common.requiredField") : undefined}
                  />

                  <TextInputController
                    name="billingAddress.street"
                    control={control}
                    required
                    html5Validation={false}
                    label={t("quoteRequestLine.generateQuoteModal.billingAddress.street")}
                    error={errors.billingAddress?.street ? t("common.requiredField") : undefined}
                  />

                  <TextInputController
                    name="billingAddress.zipCode"
                    control={control}
                    required
                    html5Validation={false}
                    label={t("quoteRequestLine.generateQuoteModal.billingAddress.zipCode")}
                    error={errors.billingAddress?.zipCode ? t("common.requiredField") : undefined}
                  />

                  <TextInputController
                    name="billingAddress.city"
                    control={control}
                    required
                    html5Validation={false}
                    label={t("quoteRequestLine.generateQuoteModal.billingAddress.city")}
                    error={errors.billingAddress?.city ? t("common.requiredField") : undefined}
                  />
                </Stack>
              </Fieldset>
              <Fieldset legend={t("quoteRequestLine.generateQuoteModal.legendContact")} flex={1}>
                <Stack gap="xs" flex={1}>
                  <Flex gap="xs">
                    <TextInputController
                      name="contact.firstName"
                      control={control}
                      required
                      html5Validation={false}
                      label={t("quoteRequestLine.generateQuoteModal.contact.firstName")}
                      error={errors.contact?.firstName ? t("common.requiredField") : undefined}
                    />
                    <TextInputController
                      name="contact.lastName"
                      control={control}
                      required
                      html5Validation={false}
                      label={t("quoteRequestLine.generateQuoteModal.contact.lastName")}
                      error={errors.contact?.lastName ? t("common.requiredField") : undefined}
                    />
                  </Flex>

                  <TextInputController
                    name="contact.email"
                    control={control}
                    required
                    html5Validation={false}
                    label={t("quoteRequestLine.generateQuoteModal.contact.email")}
                    error={errors.contact?.email ? t("common.requiredField") : undefined}
                  />
                </Stack>
              </Fieldset>
            </Flex>
          </FocusTrap>

          <Fieldset legend={t("quoteRequestLine.generateQuoteModal.legendInformations")} flex={1}>
            <Stack gap="xs" flex={1}>
              <DatePickerInput
                {...expirationDateField}
                value={expirationDateField.value}
                onChange={onHandleDateChange(expirationDateField)}
                label={t("quoteRequestLine.generateQuoteModal.expirationDate")}
                valueFormat="DD.MM.YYYY"
                clearable
              />

              <DatePickerInput
                {...shippingDelayField}
                value={shippingDelayField.value}
                onChange={onHandleDateChange(shippingDelayField)}
                label={t("quoteRequestLine.generateQuoteModal.labels.shippingDelay")}
                valueFormat="DD.MM.YYYY"
                clearable
              />

              <NumberInputController
                name="salesMarginRatio"
                control={control}
                hideControls
                label={t("quoteRequestLine.generateQuoteModal.labels.salesMarginRatio")}
              />

              <NumberInputController
                name="discountRatio"
                control={control}
                hideControls
                label={t("quoteRequestLine.generateQuoteModal.labels.discountRatio")}
              />

              <TextInputController
                name="clientReference"
                control={control}
                html5Validation={false}
                rules={{
                  maxLength: {
                    value: 40,
                    message: "La référence doit contenir au maximum 40 caractères",
                  },
                }}
                label={t("quoteRequestLine.generateQuoteModal.labels.clientReference")}
              />

              <TextareaController
                name="comment"
                control={control}
                autosize
                minRows={2}
                label={t("quoteRequestLine.generateQuoteModal.labels.comment")}
              />
            </Stack>
          </Fieldset>

          <Button type="submit" loading={isLoading}>
            {t("quoteRequestLine.generateQuoteModal.submit")}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
};
